// Pure helpers for the application feature: draft normalisation, mapping server
// blockers to wizard steps, hint arithmetic for notice and windows, and labels.
// No React and only relative imports, so tests/unit can import this file directly
// (vitest.config.ts has no '@' alias). Nothing here decides a pass or fail: the
// server checks everything again when a command runs.
import { ACTIVITY_GROUPS, CLASS_ACTIVITIES, ISOLATION_KINDS, LOCATION_CLASSIFICATIONS, PRESSURE_TEST_GROUP, PRESSURE_TEST_TYPES } from '../../domain/catalogue'
import type { ActivityGroup, PressureTestType } from '../../domain/catalogue'
import type { Blocker, DraftFields, DraftTask, HazardControl, IsolationDetails, PermitBundle, PolicyView, Registry } from '../../domain/types'
import { fromLocalInput, toLocalInput } from '../../lib/format'

type Obj = Record<string, unknown>

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v)
const str = (v: unknown): string => (typeof v === 'string' ? v : '')
const strOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null)
const boolOrNull = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null)
const intOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) ? v : null)
const strArray = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])

/** Lifecycle states in which the draft can still be changed (revise_permit_draft). */
export const EDITABLE_STATES: readonly string[] = ['draft', 'returned_for_revision']

// -----------------------------------------------------------------------------
// Draft fields
// -----------------------------------------------------------------------------
export const ROUTE_QUESTION_KEYS = ['isolation_required', 'hot_work', 'confined_space_entry', 'excavation', 'vehicle_entry'] as const
export type RouteQuestionKey = (typeof ROUTE_QUESTION_KEYS)[number]

export const ROUTE_QUESTIONS: readonly { key: RouteQuestionKey; question: string; hint: string }[] = [
  { key: 'isolation_required', question: 'Does the work need any isolation?', hint: 'Process, mechanical, electrical or instrument isolation of plant or equipment. Yes asks for the kind, the planned period and the Isolation Certificate.' },
  { key: 'hot_work', question: 'Is there any hot work?', hint: 'Welding, cutting, grinding or any other source of ignition.' },
  { key: 'confined_space_entry', question: 'Will anyone enter a vessel or confined space?', hint: 'Any entry, even partial, into a vessel, tank, pit or other confined space.' },
  { key: 'excavation', question: 'Is there any excavation?', hint: 'Any digging or ground disturbance.' },
  { key: 'vehicle_entry', question: 'Will any vehicle enter the work area?', hint: 'Cars, trucks, cranes or other mobile plant.' },
]

/** Registry equipment, used only to name a pick saved by a draft made before 0015. */
export type EquipmentList = readonly { id: string; tag: string; description: string }[]

function normaliseTask(input: unknown, index: number, equipment?: EquipmentList): DraftTask {
  const t = isObj(input) ? input : {}
  const ref = isObj(t.applicable_procedure_reference) ? t.applicable_procedure_reference : null
  const group = typeof t.activity_group === 'string' && (ACTIVITY_GROUPS as readonly string[]).includes(t.activity_group) ? (t.activity_group as ActivityGroup) : null
  const type =
    typeof t.pressure_test_type === 'string' && (PRESSURE_TEST_TYPES as readonly string[]).includes(t.pressure_test_type)
      ? (t.pressure_test_type as PressureTestType)
      : null
  // Before 0015 a task could pick equipment from the register: keep it as typed text.
  let equipmentText = strOrNull(t.equipment_text)
  if ((equipmentText === null || equipmentText.trim() === '') && typeof t.equipment_id === 'string') {
    const e = equipment?.find((x) => x.id === t.equipment_id)
    if (e) equipmentText = `${e.tag} - ${e.description}`
  }
  return {
    sequence: index + 1,
    equipment_text: equipmentText,
    description: str(t.description),
    activity_group: group,
    pressure_test_type: type,
    test_medium: strOrNull(t.test_medium),
    surface_preparation_method: strOrNull(t.surface_preparation_method),
    applicable_procedure_reference: ref ? { identity: str(ref.identity), revision: str(ref.revision), issuer: str(ref.issuer) } : null,
  }
}

/** Hours from a gas plan: the 0015 value, or a pre-0015 interval in minutes. */
function retestHoursOf(g: Obj): number | null {
  const h = g.retest_interval_hours
  if (typeof h === 'number' && Number.isFinite(h)) return h
  const m = g.retest_interval_minutes
  if (typeof m === 'number' && Number.isFinite(m)) return Math.round((m / 60) * 100) / 100
  return null
}

function normaliseHazard(input: unknown): HazardControl {
  const h = isObj(input) ? input : {}
  // An unanswered control type stays unanswered: submit_permit asks for it (F01).
  return { hazard: str(h.hazard), control: str(h.control), kind: h.kind === 'additional' || h.kind === 'standard' ? h.kind : null }
}

/**
 * The full DraftFields shape with every key present. Values are kept verbatim (no
 * trimming, no inferred defaults); only values of the wrong type become empty.
 * Key order is fixed, so JSON snapshots of two normalised drafts compare reliably.
 */
export function normaliseDraftFields(input: unknown, equipment?: EquipmentList): DraftFields {
  const v = isObj(input) ? input : {}
  const j = isObj(v.jhsep) ? v.jhsep : {}
  const s = isObj(v.simops) ? v.simops : {}
  const g = isObj(v.gas_plan) ? v.gas_plan : {}
  const tp = isObj(v.third_party) ? v.third_party : {}
  const jr = isObj(v.job_readiness) ? v.job_readiness : {}
  return {
    title: str(v.title),
    class_basis: str(v.class_basis),
    class_activities: strArray(v.class_activities),
    site_area_id: strOrNull(v.site_area_id),
    work_type_id: strOrNull(v.work_type_id),
    location_detail: str(v.location_detail),
    work_order_ref: strOrNull(v.work_order_ref),
    requested_from: strOrNull(v.requested_from),
    requested_to: strOrNull(v.requested_to),
    work_description: str(v.work_description),
    work_boundary: str(v.work_boundary),
    tools_equipment: str(v.tools_equipment),
    work_party_max: intOrNull(v.work_party_max),
    nominated_ph_person_id: strOrNull(v.nominated_ph_person_id),
    urgency_justification: strOrNull(v.urgency_justification),
    third_party: {
      // Unanswered stays null; submit_permit asks for it.
      involved: boolOrNull(tp.involved),
      hm_jsa_approved: tp.hm_jsa_approved === true,
      procedures_approved: tp.procedures_approved === true,
      equipment_approved: tp.equipment_approved === true,
      personnel_approved: tp.personnel_approved === true,
    },
    job_readiness: {
      emergency_response_adequate: jr.emergency_response_adequate === true,
      ppe_adequate: jr.ppe_adequate === true,
      personnel_credentials_adequate: jr.personnel_credentials_adequate === true,
      equipment_credentials_adequate: jr.equipment_credentials_adequate === true,
    },
    tasks: Array.isArray(v.tasks) ? v.tasks.map((t, i) => normaliseTask(t, i, equipment)) : [],
    hazards: Array.isArray(v.hazards) ? v.hazards.map(normaliseHazard) : [],
    jhsep: {
      // Unanswered stays null; the PA must choose the level (F01, QUESTION_UNANSWERED).
      level: j.level === 1 || j.level === 2 ? j.level : null,
      reference: str(j.reference),
      summary: str(j.summary),
      mopo_reviewed: j.mopo_reviewed === true,
      lessons_reviewed: j.lessons_reviewed === true,
      site_examination_done: j.site_examination_done === true,
      site_examination_note: strOrNull(j.site_examination_note),
    },
    simops: {
      nearby_activities: str(s.nearby_activities),
      conflicts_identified: str(s.conflicts_identified),
      mopo_consulted: s.mopo_consulted === true,
    },
    gas_plan: {
      required: boolOrNull(g.required),
      triggers: strArray(g.triggers),
      species: strArray(g.species),
      retest_interval_hours: retestHoursOf(g),
      continuous_monitoring: boolOrNull(g.continuous_monitoring),
      notes: str(g.notes),
    },
    isolation_required: boolOrNull(v.isolation_required),
    isolation: normaliseIsolation(v.isolation),
    hot_work: boolOrNull(v.hot_work),
    confined_space_entry: boolOrNull(v.confined_space_entry),
    excavation: boolOrNull(v.excavation),
    vehicle_entry: boolOrNull(v.vehicle_entry),
    supporting_documents: Array.isArray(v.supporting_documents)
      ? v.supporting_documents.map((d) => (isObj(d) ? { kind: str(d.kind), reason: str(d.reason) } : { kind: '', reason: '' }))
      : [],
    affected_area_ids: strArray(v.affected_area_ids),
    location_attributes_confirmed: v.location_attributes_confirmed === true,
  }
}

/** The isolation details (0031): listed kinds only, text verbatim, dates as given, questions unanswered unless answered. */
export function normaliseIsolation(input: unknown): IsolationDetails {
  const x = isObj(input) ? input : {}
  const known = new Set(ISOLATION_KINDS.map((k) => k.code))
  return {
    kinds: strArray(x.kinds).filter((k) => known.has(k)),
    reason: str(x.reason),
    planned_from: strOrNull(x.planned_from),
    planned_to: strOrNull(x.planned_to),
    certificate_documented: boolOrNull(x.certificate_documented),
    certificate_reference: strOrNull(x.certificate_reference),
  }
}

export function emptyIsolation(): IsolationDetails {
  return normaliseIsolation({})
}

/** Answering the isolation question: No clears the details, as the server refuses details on a No. */
export function withIsolationRequired(fields: DraftFields, required: boolean | null): DraftFields {
  return required === true ? { ...fields, isolation_required: true } : { ...fields, isolation_required: required, isolation: emptyIsolation() }
}

/** True when the policy version supports the isolation route (0031: routes.unsupported without "isolation"). */
export function isolationRouteSupported(policy: PolicyView | null | undefined): boolean {
  const p = findParam(policy, 'routes.unsupported')
  if (!p || p.value_status === 'unresolved' || !Array.isArray(p.value)) return false
  return !p.value.includes('isolation')
}

export function emptyDraftFields(): DraftFields {
  return normaliseDraftFields({})
}

/** A new hazard row: nothing answered yet, including the control type. */
export function emptyHazard(): HazardControl {
  return normaliseHazard({})
}

export function emptyTask(sequence: number): DraftTask {
  return { ...normaliseTask({}, 0), sequence }
}

/** Stable text form used to tell whether local edits differ from what was saved. */
export function draftSnapshot(fields: unknown): string {
  return JSON.stringify(normaliseDraftFields(fields))
}

export const EMPTY_SNAPSHOT = draftSnapshot({})

export function resequence(tasks: DraftTask[]): DraftTask[] {
  return tasks.map((t, i) => ({ ...t, sequence: i + 1 }))
}

/** The work-location classification sealed in a revision (0015), or null before 0015. */
export function sealedLocation(content: Record<string, unknown>): { code: string; label: string | null } | null {
  const code = content.location_classification
  if (typeof code !== 'string' || !code) return null
  return { code, label: typeof content.location_classification_label === 'string' ? content.location_classification_label : locationClassificationLabel(code) }
}

/** The listed class activities (0015) for codes, in the order chosen; unknown codes kept as text. */
export function classActivityLabels(codes: readonly string[], sealed?: unknown): string[] {
  const detail = Array.isArray(sealed) ? sealed.filter(isObj) : []
  return codes.map((code) => {
    const s = detail.find((d) => d.code === code)
    if (s && typeof s.label === 'string') return s.label
    return CLASS_ACTIVITIES.find((a) => a.code === code)?.label ?? `${code} (not in the list)`
  })
}

/**
 * Choosing the permit class keeps only the chosen activities of that class (note 20); the
 * ones removed are returned so the form can say so.
 */
export function withPermitClassActivities(fields: DraftFields, permitClass: string | null): { fields: DraftFields; removed: string[] } {
  const keep = fields.class_activities.filter((code) => CLASS_ACTIVITIES.find((a) => a.code === code)?.permit_class === permitClass)
  const removed = fields.class_activities.filter((code) => !keep.includes(code))
  return { fields: removed.length ? { ...fields, class_activities: keep } : fields, removed: classActivityLabels(removed) }
}

/** The label of a work-location classification code, or null when it is not listed. */
export function locationClassificationLabel(code: string | null | undefined): string | null {
  return LOCATION_CLASSIFICATIONS.find((x) => x.code === code)?.label ?? null
}

/** A task is a pressure test when its activity group is Pressure Testing (0015; ACT-070). */
export function taskIsPressureTest(task: { activity_group: string | null }): boolean {
  return task.activity_group === PRESSURE_TEST_GROUP
}

/**
 * Changing the activity group clears the details that belonged to the old group (the
 * pressure-test type and medium, the preparation method), so they are never sealed by
 * accident under a group that does not ask for them.
 */
export function withActivityGroup(task: DraftTask, group: ActivityGroup | null): DraftTask {
  return {
    ...task,
    activity_group: group,
    pressure_test_type: group === PRESSURE_TEST_GROUP ? task.pressure_test_type : null,
    test_medium: group === PRESSURE_TEST_GROUP ? task.test_medium : null,
    surface_preparation_method: group === 'Surface Preparation' ? task.surface_preparation_method : null,
  }
}

/**
 * Pressure tests and Surface Preparation need their procedure VERIFIED before
 * authorisation (ACT-069/070). Every task needs a procedure reference (0015).
 */
export function taskNeedsProcedure(task: { activity_group: string | null; is_pressure_test?: boolean | null }): boolean {
  return task.is_pressure_test === true || taskIsPressureTest(task) || task.activity_group === 'Surface Preparation'
}

/** Answering "No" to the third-party question clears the four confirmations. */
export function withThirdParty(fields: DraftFields, involved: boolean): DraftFields {
  return involved
    ? { ...fields, third_party: { ...fields.third_party, involved: true } }
    : { ...fields, third_party: { involved: false, hm_jsa_approved: false, procedures_approved: false, equipment_approved: false, personnel_approved: false } }
}

/** The four confirmations asked when a third party or subcontractor takes part (GOV-004). */
export const THIRD_PARTY_CONFIRMATIONS: readonly { key: Exclude<keyof DraftFields['third_party'], 'involved'>; label: string }[] = [
  { key: 'hm_jsa_approved', label: "Their hazard management plan or job safety analysis (HM/JSA) is approved" },
  { key: 'procedures_approved', label: 'Their procedures are approved' },
  { key: 'equipment_approved', label: 'Their equipment is approved' },
  { key: 'personnel_approved', label: 'Their personnel are approved and authorised' },
]

export const THIRD_PARTY_QUESTION = 'Is a third party or subcontractor part of the activity?'

/** Job readiness (0015): each confirmed by the PA, none pre-ticked. */
export const JOB_READINESS_QUESTION = 'I have verified the following and found them adequate for this job'
export const JOB_READINESS_CONFIRMATIONS: readonly { key: keyof DraftFields['job_readiness']; label: string }[] = [
  { key: 'emergency_response_adequate', label: 'Emergency response provisions' },
  { key: 'ppe_adequate', label: 'Personal protective equipment (PPE)' },
  { key: 'personnel_credentials_adequate', label: "Personal credentials - the people's competence and certificates for the job" },
  { key: 'equipment_credentials_adequate', label: 'Equipment credentials - certification and inspection of the equipment used' },
]

/** ISO end = start + the class maximum (whole days), or null when either is unknown. */
export function maxEndFor(fromIso: string | null, maxDays: number | null): string | null {
  if (!fromIso || maxDays === null) return null
  const t = Date.parse(fromIso)
  if (Number.isNaN(t)) return null
  return fromLocalInput(toLocalInput(new Date(t + maxDays * 86_400_000).toISOString()))
}

/**
 * Entering the planned start fills the planned end with start + the class maximum
 * (CORE-028: the maximum, not a default duration). An end the applicant shortened
 * stays, unless it is no longer after the start or now exceeds the maximum. When the
 * new start gives enough notice the urgency justification is cleared, because it is
 * asked for only for short notice.
 */
export function withRequestedFrom(
  fields: DraftFields,
  fromIso: string | null,
  opts: { maxDays: number | null; noticeOk?: boolean },
): DraftFields {
  const next: DraftFields = { ...fields, requested_from: fromIso }
  if (opts.noticeOk) next.urgency_justification = null
  const newMax = maxEndFor(fromIso, opts.maxDays)
  if (!newMax || !fromIso) return next
  const oldMax = maxEndFor(fields.requested_from, opts.maxDays)
  const end = fields.requested_to ? Date.parse(fields.requested_to) : NaN
  const autoEnd = !fields.requested_to || (oldMax !== null && fields.requested_to === oldMax)
  if (autoEnd || Number.isNaN(end) || end <= Date.parse(fromIso) || end > Date.parse(newMax)) next.requested_to = newMax
  return next
}

/**
 * Why the chosen Permit Holder's licence may not cover this work, from the registry's
 * credential scopes (0015), or null when it does or the scopes are unknown. A warning
 * only: the server checks the licence when the application is submitted.
 */
export function phScopeProblem(
  person: Registry['people'][number] | null | undefined,
  acceptRoles: string[] | null,
  areaId: string | null,
  workTypeId: string | null,
): string | null {
  if (!person || !person.credentials || !areaId) return null
  const accept = acceptRoles ?? ['PH']
  const creds = person.credentials.filter((c) => accept.includes(c.role_code))
  if (creds.length === 0) return `${person.display_name} has no current licence that can act as Permit Holder.`
  const covers = creds.some((c) =>
    c.scopes.some(
      (s) =>
        s.scope_mode === 'explicit_global' ||
        // Company-wide (0021): every unit of the company's sites; the registry lists only the caller's sites.
        s.scope_mode === 'organisation' ||
        (s.scope_mode === 'area' && s.site_area_id === areaId) ||
        (s.scope_mode === 'area_work_type' && s.site_area_id === areaId && workTypeId !== null && s.work_type_id === workTypeId),
    ),
  )
  if (covers) return null
  const areaOnly = creds.some((c) => c.scopes.some((s) => s.scope_mode === 'area_work_type' && s.site_area_id === areaId))
  return areaOnly
    ? `${person.display_name}'s Permit Holder licence covers this unit only for other work types, not the work type chosen.`
    : `${person.display_name}'s Permit Holder licence does not cover the chosen unit.`
}

/** Hours typed for the gas retest interval: a positive number (comma or point), else null. */
export function parseHoursInput(text: string): number | null {
  const t = text.trim().replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null
  const n = Number(t)
  return n > 0 ? n : null
}

/**
 * Changing the work area withdraws the classification confirmation (it was given for
 * another area's classification) and drops the new area from the affected areas.
 */
export function withArea(fields: DraftFields, areaId: string | null): DraftFields {
  if (areaId === fields.site_area_id) return fields
  return {
    ...fields,
    site_area_id: areaId,
    location_attributes_confirmed: false,
    affected_area_ids: fields.affected_area_ids.filter((a) => a !== areaId),
  }
}

/** Answering "No" to gas testing clears the triggers, species and retest interval. */
export function withGasRequired(fields: DraftFields, required: boolean): DraftFields {
  return required
    ? { ...fields, gas_plan: { ...fields.gas_plan, required: true } }
    : { ...fields, gas_plan: { ...fields.gas_plan, required: false, triggers: [], species: [], retest_interval_hours: null } }
}

export function toggleIn(list: string[], code: string, on: boolean): string[] {
  return on ? (list.includes(code) ? list : [...list, code]) : list.filter((c) => c !== code)
}

/** What still blocks the very first save (create_permit_draft needs these). */
export function createMissing(siteId: string | null, permitClass: string | null, fields: DraftFields): string[] {
  const out: string[] = []
  if (!siteId) out.push('the geographical area')
  if (permitClass !== 'A' && permitClass !== 'B') out.push('the permit class')
  if (!fields.site_area_id) out.push('the unit')
  if (!fields.work_type_id) out.push('the work type')
  return out
}

// -----------------------------------------------------------------------------
// Wizard steps and server blockers
// -----------------------------------------------------------------------------
export const STEPS = [
  { key: 'scope', no: 1, title: 'Location and class', short: 'Location' },
  { key: 'work', no: 2, title: 'The work', short: 'Work' },
  { key: 'tasks', no: 3, title: 'Tasks', short: 'Tasks' },
  { key: 'hazards', no: 4, title: 'Hazards and Job HSE plan', short: 'Hazards' },
  { key: 'routes', no: 5, title: 'Special work, gas and documents', short: 'Checks' },
  { key: 'review', no: 6, title: 'Review and submit', short: 'Submit' },
] as const
export type StepKey = (typeof STEPS)[number]['key']

export function parseStep(value: string | null | undefined): StepKey | null {
  return STEPS.find((s) => s.key === value)?.key ?? null
}

export function stepInfo(key: StepKey): (typeof STEPS)[number] {
  return STEPS.find((s) => s.key === key) ?? STEPS[0]
}

const FIELD_STEP: Record<string, StepKey> = {
  site_id: 'scope',
  permit_class: 'scope',
  site_area_id: 'scope',
  work_type_id: 'scope',
  class_basis: 'scope',
  class_activities: 'scope',
  location_detail: 'scope',
  title: 'work',
  work_order_ref: 'work',
  requested_from: 'work',
  requested_to: 'work',
  work_description: 'work',
  work_boundary: 'work',
  tools_equipment: 'work',
  work_party_max: 'work',
  nominated_ph_person_id: 'work',
  urgency_justification: 'work',
  third_party: 'work',
  tasks: 'tasks',
  hazards: 'hazards',
  jhsep: 'hazards',
  job_readiness: 'hazards',
  simops: 'hazards',
  isolation_required: 'routes',
  isolation: 'routes',
  hot_work: 'routes',
  confined_space_entry: 'routes',
  excavation: 'routes',
  vehicle_entry: 'routes',
  gas_plan: 'routes',
  supporting_documents: 'routes',
  affected_area_ids: 'routes',
  location_attributes_confirmed: 'routes',
  area_classification: 'routes',
}

/** Fallback for blockers that carry no field (codes from docs/engineering/first-slice-spec.md). */
const CODE_STEP: Record<string, StepKey> = {
  NOTICE_SHORT_NEEDS_JUSTIFICATION: 'work',
  AUTHORISATION_PERIOD_TOO_LONG: 'work',
  ROLE_COMBINATION_EXCEPTION_UNSUPPORTED: 'work',
  PRESSURE_TEST_DETAILS_REQUIRED: 'tasks',
  SURFACE_PREPARATION_DETAILS_REQUIRED: 'tasks',
  QUESTION_UNANSWERED: 'routes',
  AREA_CLASSIFICATION_UNRESOLVED: 'routes',
  NO_ISSUING_AUTHORITY: 'scope',
  CLIENT_RECORD_ONLY_BOUNDARY: 'scope',
  AUTHORITY_AMBIGUOUS: 'scope',
  // Newer server checks; they arrive with their own message and are never dropped.
  WORK_PARTY_EXCEEDS_MAXIMUM: 'work',
  WORK_TYPE_TASK_MISMATCH: 'tasks',
  AREA_CLASSIFICATION_CHANGED: 'routes',
  // 0015 form review.
  THIRD_PARTY_CONFIRMATION_MISSING: 'work',
  PROCEDURE_REFERENCE_REQUIRED: 'tasks',
  READINESS_NOT_CONFIRMED: 'hazards',
  CLASS_ACTIVITY_NOT_FOR_CLASS: 'scope',
  UNIT_NOT_CLASSIFIED: 'scope',
}

const ROUTE_STEP: Record<string, StepKey> = {
  class_a: 'scope',
  task_sop_now: 'scope',
  no_permit_job: 'scope',
  level2_jhsep: 'hazards',
  isolation: 'routes',
  hot_work: 'routes',
  confined_space_entry: 'routes',
  excavation: 'routes',
  vehicle_entry: 'routes',
}

/** Splits "fields.tasks[1].test_medium" into root "tasks", index 1 and the rest. */
export function parseFieldPath(field: string): { root: string; index: number | null; rest: string[] } {
  const parts = field.split(/[.[\]/]+/).filter(Boolean)
  if (parts[0] === 'fields' && parts.length > 1) parts.shift()
  const second = parts[1]
  const index = second !== undefined && /^\d+$/.test(second) ? Number(second) : null
  return { root: parts[0] ?? '', index, rest: parts.slice(index === null ? 1 : 2) }
}

/** The wizard step a blocker belongs to, or null when it concerns the whole application. */
export function stepForBlocker(b: Blocker): StepKey | null {
  if (typeof b.field === 'string' && b.field) {
    const step = FIELD_STEP[parseFieldPath(b.field).root]
    if (step) return step
  }
  if (b.code === 'ROUTE_NOT_SUPPORTED') {
    const route = typeof b.route === 'string' ? b.route : null
    return route ? (ROUTE_STEP[route] ?? null) : null
  }
  if (b.role === 'PH') return 'work' // the nominated Permit Holder's qualification
  return CODE_STEP[b.code] ?? null
}

/** Groups blockers by step in wizard order; the null group holds the rest. Never drops one. */
export function groupBlockersByStep(blockers: Blocker[]): { step: StepKey | null; blockers: Blocker[] }[] {
  const order: (StepKey | null)[] = [...STEPS.map((s) => s.key), null]
  return order
    .map((step) => ({ step, blockers: blockers.filter((b) => stepForBlocker(b) === step) }))
    .filter((g) => g.blockers.length > 0)
}

/** Blockers whose field is exactly this top-level key (shown under that input). */
export function blockersForRoot(blockers: Blocker[], root: string): Blocker[] {
  return blockers.filter((b) => {
    if (typeof b.field !== 'string' || !b.field) return false
    const p = parseFieldPath(b.field)
    return p.root === root && p.index === null && p.rest.length === 0
  })
}

// -----------------------------------------------------------------------------
// Time hints (the server decides; these only help the applicant)
// -----------------------------------------------------------------------------
export type NoticeStatus = 'no_start' | 'unknown' | 'short' | 'ok'

export function noticeStatus(requestedFrom: string | null, nowMs: number, minHours: number | null): NoticeStatus {
  if (!requestedFrom) return 'no_start'
  const t = Date.parse(requestedFrom)
  if (Number.isNaN(t)) return 'no_start'
  if (minHours === null) return 'unknown'
  return t < nowMs + minHours * 3_600_000 ? 'short' : 'ok'
}

export function durationText(ms: number): string {
  const totalMin = Math.round(ms / 60_000)
  const days = Math.floor(totalMin / 1440)
  const hours = Math.floor((totalMin % 1440) / 60)
  const mins = totalMin % 60
  const parts: string[] = []
  if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`)
  if (hours) parts.push(`${hours} h`)
  if (mins && !days) parts.push(`${mins} min`)
  return parts.join(' ') || '0 min'
}

export function windowSpan(fromIso: string | null | undefined, toIso: string | null | undefined): { ms: number; text: string } | null {
  if (!fromIso || !toIso) return null
  const a = Date.parse(fromIso)
  const b = Date.parse(toIso)
  if (Number.isNaN(a) || Number.isNaN(b)) return null
  const ms = b - a
  return { ms, text: ms <= 0 ? 'ends before it starts' : durationText(ms) }
}

/**
 * Argument for an optional window bound: null (let the server use the sealed value)
 * when the input is empty or still shows the original value, else the entered time.
 */
export function windowArg(input: string, originalIso: string | null | undefined): string | null {
  if (!input) return null
  if (originalIso && toLocalInput(originalIso) === input) return null
  return fromLocalInput(input)
}

// -----------------------------------------------------------------------------
// Policy values (shown with their status; never presented as approved policy)
// -----------------------------------------------------------------------------
export type PolicyParam = PolicyView['parameters'][number]

export function findParam(policy: PolicyView | null | undefined, code: string): PolicyParam | null {
  return policy?.parameters.find((p) => p.parameter_code === code) ?? null
}

export function paramNumber(p: PolicyParam | null): number | null {
  if (!p || p.value_status === 'unresolved') return null
  return typeof p.value === 'number' && Number.isFinite(p.value) ? p.value : null
}

export const VALUE_STATUS_TEXT: Record<string, string> = {
  approved: 'approved',
  source_confirmed_pending_approval: 'taken from SP-2329, not yet approved by BadrEOR',
  synthetic_fixture: 'synthetic demonstration value',
  unresolved: 'unresolved - this route stays blocked',
}

/** Roles whose licence can act in a PTW role (policy role_routes), or null when unknown. */
export function acceptRolesFor(policy: PolicyView | null | undefined, role: string): string[] | null {
  const p = findParam(policy, 'role_routes')
  if (!p || p.value_status === 'unresolved' || !isObj(p.value)) return null
  const route = p.value[role]
  return isObj(route) ? strArray(route.accept) : null
}

/** Verifier roles for a supporting-document kind: [] when the kind has no route, null when unknown. */
export function verifierRolesFor(policy: PolicyView | null | undefined, kind: string): string[] | null {
  const p = findParam(policy, 'supporting_documents.routes')
  if (!p || p.value_status === 'unresolved' || !isObj(p.value)) return null
  const route = p.value[kind]
  return isObj(route) ? strArray(route.verifier_roles) : []
}

/** People who can be nominated as Permit Holder; falls back to PH licences when the rule is unknown. */
export function phCandidates(people: Registry['people'], acceptRoles: string[] | null): Registry['people'] {
  const accept = acceptRoles ?? ['PH']
  return people.filter((p) => p.roles.some((r) => accept.includes(r)))
}

// -----------------------------------------------------------------------------
// Area classification (CORE-031)
// -----------------------------------------------------------------------------
export const CLASSIFICATION_FIELDS = [
  ['location_type', 'Location type'],
  ['hazardous_zone', 'Hazardous-area zone'],
  ['h2s_classification', 'H2S classification'],
  ['h2s_expected_max_ppm', 'Expected maximum H2S'],
  ['critical_sour', 'Critical sour'],
  ['restricted', 'Restricted area'],
  ['norm_restricted', 'NORM restricted'],
  ['classification_source', 'Source'],
  ['classification_revision', 'Source revision'],
  ['classification_status', 'Status'],
] as const

const VALUE_LABELS: Record<string, Record<string, string>> = {
  location_type: { process_facility: 'Process facility', hydrocarbon_area: 'Hydrocarbon area', non_hydrocarbon_area: 'Non-hydrocarbon area' },
  hazardous_zone: { zone_0: 'Zone 0', zone_1: 'Zone 1', zone_2: 'Zone 2', non_hazardous: 'Non-hazardous' },
  h2s_classification: { high: 'High H2S', low: 'Low H2S', non_h2s: 'Non-H2S' },
  classification_status: {
    synthetic_fixture: 'Synthetic fixture - not from a real drawing or safety case',
    evidenced: 'Evidenced from site data',
    unresolved: 'Unresolved - blocks submission',
  },
}

/** Display text for one classification value; null and missing values read "Not recorded". */
export function classificationValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not recorded'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (key === 'h2s_expected_max_ppm') return `${String(value)} ppm`
  if (typeof value === 'string') return VALUE_LABELS[key]?.[value] ?? value
  return String(value)
}

/** Labels of classification fields with no recorded value (shown as a hint; the server decides). */
export function missingClassification(area: object | null | undefined): string[] {
  if (!area) return CLASSIFICATION_FIELDS.map(([, label]) => label)
  const a = area as Obj
  return CLASSIFICATION_FIELDS.filter(([key]) => a[key] === null || a[key] === undefined || a[key] === '').map(([, label]) => label)
}

// -----------------------------------------------------------------------------
// Status labels (always words, never colour alone)
// -----------------------------------------------------------------------------
export type ToneName = 'neutral' | 'info' | 'ok' | 'warn' | 'bad'

export const PROCEDURE_STATUS: Record<string, { label: string; tone: ToneName }> = {
  verified: { label: 'Procedure verified', tone: 'ok' },
  unverified: { label: 'Procedure not verified', tone: 'warn' },
  not_applicable: { label: 'No procedure needed', tone: 'neutral' },
}

export const EVIDENCE_STATUS: Record<string, string> = {
  referenced: 'Evidence referenced',
  missing: 'Evidence missing',
  not_applicable: 'No evidence needed',
}

export const RECORD_STATUS: Record<string, { label: string; tone: ToneName }> = {
  recorded: { label: 'Recorded - not verified', tone: 'warn' },
  verified: { label: 'Verified', tone: 'ok' },
  withdrawn: { label: 'Withdrawn', tone: 'bad' },
}

export const PLANNING_BASIS_LABELS: Record<string, string> = {
  frozen_plan: 'In the frozen plan',
  urgent_justified: 'Urgent - justification accepted',
}

export const EXAM_KIND_LABELS: Record<string, string> = {
  pa_rs_pre_authorisation: 'PA/RS visit before authorisation',
  // Exam kinds keep their historic aa_ codes; the RS makes these visits from 0016.
  aa_first_day: 'RS visit on the first day',
  aa_daily: 'RS daily visit',
  aa_revalidation: 'RS visit at revalidation',
}

/** "Oxygen (O2)", but "Flammable (LEL)" rather than repeating a code already in the label. */
export function speciesLabel(options: readonly { code: string; label: string }[], code: string): string {
  const label = options.find((o) => o.code === code)?.label
  if (!label) return code
  return label.includes(`(${code})`) ? label : `${label} (${code})`
}

export function yesNo(value: boolean | null | undefined, unanswered = 'Not answered'): string {
  return value === true ? 'Yes' : value === false ? 'No' : unanswered
}

// -----------------------------------------------------------------------------
// Declarations (what the signer states; the server records who and when)
// -----------------------------------------------------------------------------
export const SUBMIT_DECLARATION =
  'I confirm this application and its Job HSE plan are complete and correct as far as I know. I understand that submitting seals this version for review and does not authorise any work.'

export const AUTHORISE_DECLARATION = 'I authorise this permit for use pending daily validation; I will not be doing this work myself.'

// -----------------------------------------------------------------------------
// Misc
// -----------------------------------------------------------------------------
/** A string argument hint from AvailableAction.args. */
export function argString(args: Record<string, unknown> | undefined | null, key: string): string | null {
  const v = args?.[key]
  return typeof v === 'string' && v ? v : null
}

type Review = PermitBundle['reviews'][number]

/**
 * The return-for-revision review that the current draft answers. It stays relevant
 * after the first save, which moves the state from returned_for_revision back to draft.
 */
export function latestReturn(bundle: Pick<PermitBundle, 'permit' | 'draft' | 'reviews'>): Review | null {
  if (!EDITABLE_STATES.includes(bundle.permit.lifecycle_state)) return null
  const basedOn =
    bundle.draft?.based_on_revision_id ?? (bundle.permit.lifecycle_state === 'returned_for_revision' ? bundle.permit.current_revision_id : null)
  if (!basedOn) return null
  return (
    bundle.reviews
      .filter((r) => r.review_kind === 'return_for_revision' && r.revision_id === basedOn)
      .sort((a, b) => b.attestation.server_signed_at.localeCompare(a.attestation.server_signed_at))[0] ?? null
  )
}

/** current row version from a PW002 STALE_VERSION detail. */
export function staleCurrentVersion(detail: unknown): number | null {
  return isObj(detail) && typeof detail.current === 'number' ? detail.current : null
}

// -----------------------------------------------------------------------------
// Device copy of unsaved edits (localStorage; removed when the demo is left)
// -----------------------------------------------------------------------------
export type DraftBackup = {
  v: 1
  fields: DraftFields
  base_row_version: number | null
  site_id: string | null
  permit_class: 'A' | 'B' | null
  written_at: string
}

export function backupKey(sandbox: string | null, persona: string | null, permitId: string | null): string {
  // The 'ptw-demo:' prefix means leaving the demo clears these copies too (src/lib/session.ts).
  return `ptw-demo:draft-copy:${sandbox ?? '-'}:${persona ?? '-'}:${permitId ?? 'new'}`
}

export function parseBackup(text: string | null | undefined): DraftBackup | null {
  if (!text) return null
  try {
    const o: unknown = JSON.parse(text)
    if (!isObj(o) || o.v !== 1 || !isObj(o.fields) || typeof o.written_at !== 'string') return null
    return {
      v: 1,
      fields: normaliseDraftFields(o.fields),
      base_row_version: typeof o.base_row_version === 'number' ? o.base_row_version : null,
      site_id: typeof o.site_id === 'string' ? o.site_id : null,
      permit_class: o.permit_class === 'A' || o.permit_class === 'B' ? o.permit_class : null,
      written_at: o.written_at,
    }
  } catch {
    return null
  }
}
